import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { products, productColors } from '@/mocks/products';

const ITEMS_PER_PAGE = 8;
const CATALOG_STATE_KEY = 'macave_catalog_state';

interface CatalogState {
  activeFilter: string;
  searchQuery: string;
  currentPage: number;
}

function loadCatalogState(): CatalogState {
  try {
    const raw = sessionStorage.getItem(CATALOG_STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        activeFilter: parsed.activeFilter || 'todos',
        searchQuery: parsed.searchQuery || '',
        currentPage: parsed.currentPage || 1,
      };
    }
  } catch { /* ignorado */ }
  return { activeFilter: 'todos', searchQuery: '', currentPage: 1 };
}

function saveCatalogState(state: CatalogState) {
  try {
    sessionStorage.setItem(CATALOG_STATE_KEY, JSON.stringify(state));
  } catch { /* ignorado */ }
}

const filterCategories = [
  { id: 'todos', label: 'Todos' },
  { id: 'bebe', label: 'Bebê' },
  { id: 'menina', label: 'Menina' },
  { id: 'menino', label: 'Menino' },
  { id: 'acessorios', label: 'Acessórios' },
  { id: 'lancamentos', label: 'Lançamentos' },
  { id: 'calcados', label: 'Calçados' },
  { id: 'pijamas', label: 'Pijamas' },
  { id: 'praia', label: 'Praia e Verão' },
];

function CatalogSection() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(() => loadCatalogState().activeFilter);
  const [searchQuery, setSearchQuery] = useState(() => loadCatalogState().searchQuery);
  const [currentPage, setCurrentPage] = useState(() => loadCatalogState().currentPage);
  const [selectedSizes, setSelectedSizes] = useState<Record<string, string>>({});
  const [selectedQtys, setSelectedQtys] = useState<Record<string, number>>({});
  const [addedFlash, setAddedFlash] = useState<Record<string, boolean>>({});
  const [imageIndices, setImageIndices] = useState<Record<string, number>>({});
  const [animKey, setAnimKey] = useState(0);
  const touchStartX = useRef(0);
  const { addItem } = useCart();

  useEffect(() => {
    const handler = (e: Event) => {
      const { category } = (e as CustomEvent<{ category: string }>).detail;
      setActiveFilter(category);
      setCurrentPage(1);
      setAnimKey((k) => k + 1);
      saveCatalogState({ activeFilter: category, searchQuery: '', currentPage: 1 });
    };
    window.addEventListener('catalog:filter', handler);
    return () => window.removeEventListener('catalog:filter', handler);
  }, []);

  const persistState = (overrides: Partial<CatalogState>) => {
    const state: CatalogState = { activeFilter, searchQuery, currentPage, ...overrides };
    saveCatalogState(state);
  };

  const filteredProducts = useMemo(() => {
    let result = activeFilter === 'todos'
      ? products
      : products.filter((p) => p.category === activeFilter);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.categoryLabel.toLowerCase().includes(q)
      );
    }

    return result;
  }, [activeFilter, searchQuery]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const handleFilterChange = (catId: string) => {
    setActiveFilter(catId);
    setCurrentPage(1);
    setAnimKey((k) => k + 1);
    persistState({ activeFilter: catId, currentPage: 1 });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    setAnimKey((k) => k + 1);
    persistState({ searchQuery: value, currentPage: 1 });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setAnimKey((k) => k + 1);
    persistState({ currentPage: page });
    window.scrollTo({ top: document.getElementById('catalogo')?.offsetTop ?? 0, behavior: 'smooth' });
  };

  const handleSelectSize = (productId: string, size: string) => {
    setSelectedSizes((prev) => ({ ...prev, [productId]: size }));
  };

  const handleSelectQty = (productId: string, qty: number) => {
    setSelectedQtys((prev) => ({ ...prev, [productId]: qty }));
  };

  const handleAddToCart = (e: React.MouseEvent, product: typeof products[0]) => {
    e.stopPropagation();
    const size = selectedSizes[product.id] || product.sizes?.[0] || '';
    const qty = selectedQtys[product.id] || 1;
    addItem({
      productId: product.id,
      productName: product.name,
      categoryLabel: product.categoryLabel,
      image: product.image,
      size,
      price: product.price,
      quantity: qty,
    });
    setAddedFlash((prev) => ({ ...prev, [product.id]: true }));
    setTimeout(() => {
      setAddedFlash((prev) => ({ ...prev, [product.id]: false }));
    }, 1500);
  };

  const handlePrevImage = (e: React.MouseEvent, productId: string, total: number) => {
    e.stopPropagation();
    setImageIndices((prev) => {
      const current = prev[productId] ?? 0;
      return { ...prev, [productId]: current === 0 ? total - 1 : current - 1 };
    });
  };

  const handleNextImage = (e: React.MouseEvent, productId: string, total: number) => {
    e.stopPropagation();
    setImageIndices((prev) => {
      const current = prev[productId] ?? 0;
      return { ...prev, [productId]: current === total - 1 ? 0 : current + 1 };
    });
  };

  const handleProductClick = (productId: string) => {
    navigate(`/produto/${productId}`);
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <section id="catalogo" className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-10 md:mb-12">
          <span className="catalog-animate inline-block text-xs font-medium text-primary-700 tracking-wider uppercase mb-2">
            Nosso Catálogo
          </span>
          <h2 className="catalog-animate font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
            Catálogo de Produtos
          </h2>
          <p className="catalog-animate text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Escolha as peças favoritas, selecione o tamanho e adicione ao carrinho.
          </p>
        </div>

        {/* Search */}
        <div className="catalog-animate flex justify-center mb-6 md:mb-8">
          <div className="relative w-full max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              placeholder="Buscar por nome, categoria ou descrição..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-9 pr-9 py-2.5 rounded-full text-sm border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="catalog-animate flex flex-wrap items-center justify-center gap-2 mb-8 md:mb-10">
          {filterCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleFilterChange(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeFilter === cat.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-background-200 text-foreground-700 hover:bg-background-300'
              }`}
            >
              {cat.label}
              {activeFilter === cat.id && !searchQuery && (
                <span className="ml-1.5 text-xs opacity-80">({filteredProducts.length})</span>
              )}
            </button>
          ))}
        </div>

        {/* Results count */}
        <div className="catalog-animate text-center mb-6">
          <p className="text-xs text-foreground-500">
            {filteredProducts.length === 0
              ? 'Nenhum produto encontrado'
              : `Exibindo ${(currentPage - 1) * ITEMS_PER_PAGE + 1}–${Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length)} de ${filteredProducts.length} produtos`}
            {activeFilter !== 'todos' && !searchQuery && <> em {filterCategories.find((c) => c.id === activeFilter)?.label}</>}
            {searchQuery && <> para "{searchQuery}"</>}
          </p>
        </div>

        {/* Products Grid */}
        {paginatedProducts.length > 0 ? (
          <div key={animKey} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
            {paginatedProducts.map((product, index) => {
              const currentSize = selectedSizes[product.id] || product.sizes?.[0] || '';
              const currentQty = selectedQtys[product.id] || 1;
              const isAdded = addedFlash[product.id];
              const imgs = product.images || [product.image];
              const imgIdx = imageIndices[product.id] ?? 0;
              const currentImage = imgs[imgIdx] || imgs[0];

              return (
                <div
                  key={product.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleProductClick(product.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleProductClick(product.id); }}
                  className="catalog-item bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:shadow-md transition-shadow duration-300 cursor-pointer group"
                  style={{ animationDelay: `${index * 75}ms` }}
                  data-product-shop
                >
                  {/* Image with navigation */}
                  <div
                    className="relative aspect-[3/4] overflow-hidden"
                    onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                    onTouchEnd={(e) => {
                      const diff = touchStartX.current - e.changedTouches[0].clientX;
                      if (Math.abs(diff) > 40) {
                        e.stopPropagation();
                        setImageIndices((prev) => {
                          const cur = prev[product.id] ?? 0;
                          return { ...prev, [product.id]: diff > 0 ? (cur === imgs.length - 1 ? 0 : cur + 1) : (cur === 0 ? imgs.length - 1 : cur - 1) };
                        });
                      }
                    }}
                  >
                    <img
                      src={currentImage}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {product.isNew && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary-600 text-white text-xs font-medium z-10">
                        Lançamento
                      </span>
                    )}

                    {/* Image nav arrows */}
                    {imgs.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => handlePrevImage(e, product.id, imgs.length)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-white/80 text-foreground-700 hover:bg-white hover:text-foreground-900 transition-all md:opacity-0 md:group-hover:opacity-100 z-10"
                        >
                          <i className="ri-arrow-left-s-line text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleNextImage(e, product.id, imgs.length)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-white/80 text-foreground-700 hover:bg-white hover:text-foreground-900 transition-all md:opacity-0 md:group-hover:opacity-100 z-10"
                        >
                          <i className="ri-arrow-right-s-line text-sm" />
                        </button>

                        {/* Dots */}
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
                          {imgs.map((_, i) => (
                            <span
                              key={i}
                              className={`block rounded-full transition-all duration-300 ${
                                i === imgIdx
                                  ? 'w-2 h-2 bg-white shadow-sm'
                                  : 'w-1.5 h-1.5 bg-white/60'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <span className="text-xs font-medium text-primary-700">{product.categoryLabel}</span>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mt-0.5 mb-1 group-hover:text-primary-700 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2 mb-2">
                      {product.description}
                    </p>

                    {product.price && (
                      <p className="text-base font-semibold text-primary-700 mb-3">
                        R$ {product.price.toFixed(2)}
                      </p>
                    )}

                    {/* Colors */}
                    <div className="flex items-center gap-1.5 mb-2">
                      {product.colors.map((colorName) => {
                        const color = productColors.find((c) => c.name === colorName);
                        return (
                          <span
                            key={colorName}
                            className="w-4 h-4 rounded-full border border-background-300"
                            style={{ backgroundColor: color?.value || '#ddd' }}
                            title={colorName}
                          />
                        );
                      })}
                    </div>

                    {/* Size + Qty */}
                    <div className="flex items-center gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={currentSize}
                        onChange={(e) => handleSelectSize(product.id, e.target.value)}
                        className="text-xs px-2 py-1.5 rounded-md border border-background-300 bg-background-50 text-foreground-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
                      >
                        {product.sizes.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleSelectQty(product.id, Math.max(1, currentQty - 1)); }}
                          className="w-6 h-6 flex items-center justify-center rounded-md bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors"
                        >
                          <i className="ri-subtract-line text-xs" />
                        </button>
                        <span className="text-xs font-medium text-foreground-900 w-5 text-center">
                          {currentQty}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleSelectQty(product.id, currentQty + 1); }}
                          className="w-6 h-6 flex items-center justify-center rounded-md bg-background-200 text-foreground-700 hover:bg-background-300 transition-colors"
                        >
                          <i className="ri-add-line text-xs" />
                        </button>
                      </div>
                    </div>

                    {/* Add to Cart */}
                    <button
                      type="button"
                      onClick={(e) => handleAddToCart(e, product)}
                      className={`w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                        isAdded
                          ? 'bg-accent-500 text-white'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    >
                      <i className={isAdded ? 'ri-check-line' : 'ri-add-line'} />
                      {isAdded ? 'Adicionado!' : 'Adicionar ao carrinho'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <i className="ri-search-line text-4xl text-foreground-300 mb-3 block" />
            <p className="text-foreground-500 text-sm mb-2">Nenhum produto encontrado.</p>
            <button
              type="button"
              onClick={() => { setSearchQuery(''); setActiveFilter('todos'); setCurrentPage(1); }}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              Limpar filtros e buscar novamente
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="catalog-animate flex items-center justify-center gap-1.5 mt-10 md:mt-14">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            {getPageNumbers().map((page, i) =>
              page === '...' ? (
                <span
                  key={`ellipsis-${i}`}
                  className="w-9 h-9 flex items-center justify-center text-sm text-foreground-400"
                >
                  ...
                </span>
              ) : (
                <button
                  key={`page-${page}`}
                  type="button"
                  onClick={() => handlePageChange(page as number)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-primary-600 text-white'
                      : 'text-foreground-700 bg-background-100 hover:bg-background-200'
                  }`}
                >
                  {page}
                </button>
              )
            )}

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>

      <style>{`
        .catalog-animate {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        .catalog-item {
          animation: catalogFadeIn 0.5s ease-out forwards;
          opacity: 0;
          transform: translateY(1rem);
        }
        @keyframes catalogFadeIn {
          from {
            opacity: 0;
            transform: translateY(1rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}

export default CatalogSection;